import {
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    useFormaliteService,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import { type DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { Accordion, ReactGridContainer } from '@europrocurement/flexy-components';
import { dateToISOYmd, stringToHexColor } from '@europrocurement/l2d-utils';
import { type NavigateFunction } from 'react-router';
import { useDispatch } from 'react-redux';
import { type ResizeHandleAxis } from 'react-grid-layout';
import {
    type GroupesDeTravailJsonldUtilisateurReadDomaineRead,
    type UtilisateursJsonldUserworkgroupRead,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import ChartJsBar, {
    type ChartJsBarDataType,
} from '@europrocurement/flexy-components/components/molecules/ChartJsBar/ChartJsBar';
import FiltersDashboard from '../../components/FiltersDashboard/FiltersDashboard';
import DossierStatusWidgetDashboard from '../../components/DossierStatusWidgetDashboard/DossierStatusWidgetDashboard';
import GreffesStatusWidgetDashboard from '../../components/GreffesStatusWidgetDashboard/GreffesStatusWidgetDashboard';

const COLUMN_RESIZE_HANDLES: ResizeHandleAxis[] = ['e', 'w'];

const OUTER_MANAGER_DASHBOARD_LAYOUT = [
    {
        i: 'widget-dossier-status',
        x: 0,
        y: 0,
        w: 4,
        h: 100,
        minW: 2,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
    {
        i: 'widget-greffes-status',
        x: 4,
        y: 0,
        w: 4,
        h: 100,
        minW: 2,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
    {
        i: 'widget-bar-chart',
        x: 8,
        y: 0,
        w: 4,
        h: 100,
        minW: 2,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
];

type FormalistDashboardProps = {
    title: string;
    formalistId: number;
    listeDossiersPath: string;
    navigate: NavigateFunction;
    formalistName?: string;
};

export type DashboardFiltersType = {
    startDate: string;
    endDate?: string;
};

const FormalistManagerDashboard: React.FunctionComponent<FormalistDashboardProps> = function ({
    title,
    formalistId,
    listeDossiersPath,
    navigate,
    formalistName,
}) {
    const { palette } = useTheme();
    const { dossierOpenSearchApi } = useFormaliteService();
    const { groupesTravailApi, utilisateursGroupeTravailApi } = useUtilisateurService();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [statusDossierOpenSearch, setStatusDossierOpenSearch] =
        useState<DossierOpenSearchJsonldOpensearchDossierStatutRead[]>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [userGroups, setUserGroups] = useState<
        GroupesDeTravailJsonldUtilisateurReadDomaineRead[]
    >([]);
    const [usersInGroups, setUsersInGroups] = useState<UtilisateursJsonldUserworkgroupRead[]>([]);
    const [chartData, setChartData] = useState<ChartJsBarDataType>();

    // Filters
    const initialStartDate = useMemo(() => {
        const DAYS_OFFSET = 30;
        const date = new Date();
        date.setDate(date.getDate() - DAYS_OFFSET);
        return dateToISOYmd(date);
    }, []);
    const [filters, setFilters] = useState<DashboardFiltersType>({
        startDate: initialStartDate,
        endDate: undefined,
    });

    // Retriveve current user referent groups
    useEffect(() => {
        if (formalistId) {
            groupesTravailApi
                .apiGroupestravailGetCollection({
                    iduserReferent: formalistId.toString(),
                })
                .then((res) => {
                    setUserGroups(res.data['hydra:member']);
                })
                .catch((err) => {
                    console.error(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération de vos groupes',
                        {
                            variant: 'error',
                        },
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalistId]);

    // retrieve users linked to the user referent groups
    useEffect(() => {
        if (userGroups.length > 0) {
            utilisateursGroupeTravailApi
                .apiUtilisateurgroupetravailGetCollection({
                    groupetravail2: userGroups.map((userGroup) => userGroup['@id'] as string),
                })
                .then((res) => {
                    setUsersInGroups(
                        res.data['hydra:member']
                            .filter((userGrp) => userGrp.user!.id !== formalistId)
                            .map((userGrp) => userGrp.user!),
                    );
                })
                .catch((err) => {
                    console.error(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération des utilisateurs de vos groupes',
                        {
                            variant: 'error',
                        },
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGroups]);

    useEffect(() => {
        dossierOpenSearchApi
            .apiOpensearchFormalistesGetCollection({
                formalisteId: [...usersInGroups.map((user) => user.id as number), formalistId],
                startDate: filters.startDate,
                endDate: filters.endDate,
            })
            .then((res) => {
                const newChartData: ChartJsBarDataType = {
                    labels: [],
                    datasets: [
                        {
                            label: 'Nombre de dossiers',
                            data: [],
                            backgroundColor: [],
                            borderWidth: 1,
                        },
                    ],
                };

                res.data['hydra:member'].map((dossFormalist) => {
                    newChartData.labels.push(dossFormalist.nomComplet as string);
                    newChartData.datasets[0].data.push(dossFormalist.childsCount as number);
                    newChartData.datasets[0].backgroundColor.push(
                        stringToHexColor(
                            ((dossFormalist.nomComplet as string) + dossFormalist.childsCount ||
                                0) as string,
                        ),
                    );
                    return true;
                });
                setChartData(newChartData);
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar(
                    'Une erreur est survenue lors de la récupération des dossiers par utilisateurs',
                    {
                        variant: 'error',
                    },
                );
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersInGroups, filters]);

    // Formalist label used to display info in table filter
    const formalistLabel = usersInGroups.length > 0 ? 'Multiple' : formalistName;

    const handleDossierStatusClick = (statusId?: number | null) => {
        if (!statusId) {
            return;
        }
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'formalisteId',
                value: [formalistId, ...usersInGroups.map((user) => user.id as number)],
            },
        });
        if (formalistLabel) {
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                payload: { key: 'formalisteId', label: formalistLabel },
            });
        }
        // dispatch  dossier status to redux and open the dossier list
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPreFilter`,
            payload: {
                filters: [
                    {
                        field: 'statutId',
                        value: statusId,
                    },
                ],
            },
        });

        navigate(`/${listeDossiersPath}`);
    };

    const fetchDossierStatus = async () => {
        setIsSubmitting(true);
        const dossierStatusResponse = await dossierOpenSearchApi.apiOpensearchStatusesGetCollection(
            {
                formalisteId: [formalistId, ...usersInGroups.map((user) => user.id as number)],
                itemsPerPage: 20,
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
        );

        setIsSubmitting(false);
        if (dossierStatusResponse.status === 200) {
            return dossierStatusResponse.data['hydra:member'];
        }
        enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
            variant: 'error',
        });
        return [];
    };

    // refetch dossierStatus on filter or formalistId update
    useEffect(() => {
        fetchDossierStatus().then((res) => setStatusDossierOpenSearch(res));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalistId, usersInGroups, filters.startDate, filters.endDate]);

    const tools = (
        <FiltersDashboard
            isSubmitting={isSubmitting}
            setFilters={setFilters}
            initialStartDate={initialStartDate}
        />
    );

    const columnSx = {
        maxHeight: 'calc(100vh + 120px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        '&:hover .outer-column-drag-handle': { opacity: 1 },
    } as const;

    const dragHandleSx = {
        cursor: 'grab',
        height: '20px',
        width: '100%',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.5,
        '&::before': {
            content: '"⠿ ⠿"',
            fontSize: '14px',
            color: palette.grey[400],
            letterSpacing: '4px',
        },
    } as const;

    return (
        <Accordion
            title={title}
            expandLabel={tools}
            expanded
            expandColor={palette.secondary}
            sx={{ boxShadow: 'unset', border: 'none' }}
        >
            <Box sx={{ maxHeight: 'calc(100vh + 100px)', overflow: 'hidden' }}>
                <ReactGridContainer
                    localStorageName="grid-formalist-manager-dashboard-outer"
                    layoutProps={OUTER_MANAGER_DASHBOARD_LAYOUT}
                    gridconfig={{ cols: 12, rowHeight: 10 }}
                    dragConfig={{ handle: '.outer-column-drag-handle' }}
                >
                    <Box
                        key="widget-dossier-status"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        {statusDossierOpenSearch && (
                            <DossierStatusWidgetDashboard
                                statusDossierOpenSearch={statusDossierOpenSearch}
                                handleDossierStatusClick={handleDossierStatusClick}
                            />
                        )}
                    </Box>
                    <Box
                        key="widget-greffes-status"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        <GreffesStatusWidgetDashboard
                            formalistIds={[
                                formalistId,
                                ...usersInGroups.map((user) => user.id as number),
                            ]}
                            filters={filters}
                            listeDossiersPath={listeDossiersPath}
                            localStorageName="grid-status-greffes-formalist-manager"
                            formalistName={formalistLabel}
                        />
                    </Box>
                    <Box
                        key="widget-bar-chart"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        <Box sx={{ overflow: 'hidden' }}>
                            <ReactGridContainer
                                localStorageName="grid-bar-chart-formalist-manager"
                                gridconfig={{ cols: 1, rowHeight: 50 }}
                                layoutProps={[
                                    {
                                        i: 'chartJs-bar-formalist',
                                        x: 0,
                                        y: 0,
                                        w: 1,
                                        h: 8,
                                        isResizable: true,
                                        resizeHandles: ['s', 'n'] as ResizeHandleAxis[],
                                    },
                                ]}
                            >
                                <Box
                                    key="chartJs-bar-formalist"
                                    sx={{ height: '100%' }}
                                >
                                    {chartData && (
                                        <ChartJsBar
                                            direction="y"
                                            displayLegend={false}
                                            displayTitle
                                            title="Nombre de dossiers par formaliste"
                                            data={chartData}
                                        />
                                    )}
                                </Box>
                            </ReactGridContainer>
                        </Box>
                    </Box>
                </ReactGridContainer>
            </Box>
        </Accordion>
    );
};

export default FormalistManagerDashboard;
